import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Card, Chip, Button, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress, Alert,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    IconButton, FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import {
    CheckCircleOutline as ApproveIcon, CancelOutlined as RejectIcon,
    Edit as EditIcon, Close as CloseIcon, Save as SaveIcon,
} from '@mui/icons-material';
import { publicGetData, publicPostData, publicPutData } from '../../services/publicAxiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

function fmtDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function extractErrorMessage(err, fallback) {
    const msg = err?.response?.data?.message;
    if (typeof msg === 'string') return msg;
    if (msg?.msg) return msg.msg;
    return err?.response?.data?.message?.toString?.() || fallback;
}

const isEntryEditable = (e) => e.external_can_edit && (!e.l2_status || e.l2_status === 'PENDING');

// Mirrors the backend's isWorkLike check (timesheet.controller.js) — only Working
// entries and WFH-leave entries get routed through L2 review; Weekend/Holiday/other
// Leave rows are shown for context only and never carry a real l2_approver_id.
const isWorkLikeEntry = (e) => e.day_type === 'Working' || (e.day_type === 'Leave' && e.leave_type === 'Work From Home');

export default function TimesheetApprovalLink() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [timesheet, setTimesheet] = useState(null);

    const [rejectDlg, setRejectDlg] = useState({ open: false, target: null, comment: '', err: '' });
    const [approveComment, setApproveComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [actionResult, setActionResult] = useState(''); // success banner text
    const [actionError, setActionError] = useState('');

    const [editingEntryId, setEditingEntryId] = useState(null);
    const [editDraft, setEditDraft] = useState({ hours_worked: '', is_billable: true });
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    const load = useCallback(async () => {
        if (!token) {
            setLoadError('This link is missing its approval token.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError('');
        try {
            const res = await publicGetData(API_ENDPOINTS.getApprovalLinkTimesheet(id, token));
            setTimesheet(res.result);
        } catch (err) {
            setLoadError(extractErrorMessage(err, 'This link is invalid or has expired.'));
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => { load(); }, [load]);

    const isCompanyScope = !!timesheet?.level2_approver_id;

    const handleApproveWhole = async () => {
        setBusy(true);
        setActionError('');
        try {
            await publicPostData(API_ENDPOINTS.approveApprovalLinkTimesheet(id, token), { comments: approveComment.trim() });
            setActionResult('You approved this timesheet.');
        } catch (err) {
            setActionError(extractErrorMessage(err, 'Could not approve this timesheet.'));
        } finally {
            setBusy(false);
        }
    };

    const openRejectDlg = (target) => setRejectDlg({ open: true, target, comment: '', err: '' });
    const closeRejectDlg = () => setRejectDlg({ open: false, target: null, comment: '', err: '' });

    const confirmReject = async () => {
        if (!rejectDlg.comment.trim()) {
            setRejectDlg((p) => ({ ...p, err: 'A rejection reason is required.' }));
            return;
        }
        setBusy(true);
        setActionError('');
        try {
            if (rejectDlg.target === 'whole') {
                await publicPostData(API_ENDPOINTS.rejectApprovalLinkTimesheet(id, token), { comments: rejectDlg.comment.trim() });
                closeRejectDlg();
                setActionResult('You rejected this timesheet. The employee has been notified to make corrections.');
            } else {
                // Rejecting a single entry does not finalize the timesheet while
                // siblings are still pending — reload and keep reviewing them
                // instead of jumping to the terminal "done" screen. Only when the
                // backend reports every entry has now been reviewed do we show that.
                const res = await publicPostData(API_ENDPOINTS.rejectApprovalLinkEntry(rejectDlg.target, token), { comments: rejectDlg.comment.trim() });
                closeRejectDlg();
                if (res?.result?.allEntriesResolved) {
                    setActionResult('You rejected this entry. All entries have now been reviewed and the timesheet has been returned to the employee for correction.');
                } else {
                    await load();
                }
            }
        } catch (err) {
            setRejectDlg((p) => ({ ...p, err: extractErrorMessage(err, 'Could not reject.') }));
        } finally {
            setBusy(false);
        }
    };

    const handleApproveEntry = async (entryId) => {
        setBusy(true);
        setActionError('');
        try {
            await publicPostData(API_ENDPOINTS.approveApprovalLinkEntry(entryId, token), { comments: '' });
            await load(); // refresh remaining entries' statuses
        } catch (err) {
            setActionError(extractErrorMessage(err, 'Could not approve this entry.'));
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (entry) => {
        setEditingEntryId(entry.id);
        setEditError('');
        setEditDraft({
            hours_worked: entry.hours_worked ?? '',
            is_billable: !!entry.is_billable,
        });
    };

    const cancelEdit = () => {
        setEditingEntryId(null);
        setEditError('');
    };

    const saveEdit = async (entryId) => {
        if (editDraft.hours_worked !== '' && Number(editDraft.hours_worked) <= 0) {
            setEditError('Hours must be greater than 0.');
            return;
        }
        setEditSaving(true);
        setEditError('');
        try {
            const payload = { is_billable: editDraft.is_billable };
            if (editDraft.hours_worked !== '') payload.hours_worked = Number(editDraft.hours_worked);
            await publicPutData(API_ENDPOINTS.editApprovalLinkEntry(entryId, token), payload);
            setEditingEntryId(null);
            await load();
        } catch (err) {
            setEditError(extractErrorMessage(err, 'Could not save changes.'));
        } finally {
            setEditSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box maxWidth={480} mx="auto" mt={8} px={2}>
                <Alert severity="error">{loadError}</Alert>
            </Box>
        );
    }

    if (actionResult) {
        return (
            <Box maxWidth={480} mx="auto" mt={8} px={2}>
                <Alert severity="success">{actionResult}</Alert>
            </Box>
        );
    }

    // Show the full week — COMPANY-scope approvers are approving the whole
    // timesheet, not just work entries, so Weekend/Leave rows stay visible for
    // context (their Hours/Type cells are blanked below since there's nothing
    // meaningful to show there).
    const entries = timesheet?.entries || [];
    const pendingEntries = entries.filter((e) => !e.l2_status || e.l2_status === 'PENDING');

    return (
        <Box maxWidth={1000} mx="auto" my={4} px={2}>
            <Card sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6">{timesheet.employee?.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Week of {fmtDate(timesheet.week_start_date)} – {fmtDate(timesheet.week_end_date)}
                </Typography>
                <Chip size="small" label={timesheet.status} sx={{ mt: 1 }} />
            </Card>

            {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
            {editError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>{editError}</Alert>}

            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Project / Task</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Hours</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.map((e) => {
                            const editing = editingEntryId === e.id;
                            return (
                                <TableRow key={e.id}>
                                    <TableCell>{fmtDate(e.entry_date)}</TableCell>
                                    <TableCell>
                                        {e.project_id ? (
                                            <>
                                                <Typography sx={{ fontWeight: 700, fontSize: 'inherit' }}>{e.project_name}</Typography>
                                                {e.task_name && (
                                                    <Typography sx={{ fontWeight: 400, fontSize: 'inherit', color: 'text.secondary' }}>{e.task_name}</Typography>
                                                )}
                                            </>
                                        ) : (
                                            <>{e.day_type}{e.leave_type ? ` (${e.leave_type})` : ''}</>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>{e.work_description}</TableCell>
                                    <TableCell sx={{ minWidth: 100 }}>
                                        {e.day_type !== 'Working' ? null : editing ? (
                                            <TextField
                                                size="small" type="number"
                                                value={editDraft.hours_worked}
                                                onChange={(ev) => setEditDraft((p) => ({ ...p, hours_worked: ev.target.value }))}
                                            />
                                        ) : (
                                            <>{e.hours_worked}{e.overtime_hours ? ` (+${e.overtime_hours} OT)` : ''}</>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {e.day_type !== 'Working' ? null : editing ? (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={editDraft.is_billable}
                                                        onChange={(ev) => setEditDraft((p) => ({ ...p, is_billable: ev.target.checked }))}
                                                    />
                                                }
                                                label={editDraft.is_billable ? 'Billable' : 'Non-Billable'}
                                            />
                                        ) : (e.is_billable ? 'Billable' : 'Non-Billable')}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {editing ? (
                                            <>
                                                <IconButton size="small" color="primary" disabled={editSaving} onClick={() => saveEdit(e.id)}>
                                                    <SaveIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" disabled={editSaving} onClick={cancelEdit}>
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <>
                                                {isEntryEditable(e) && (
                                                    <Tooltip title="Edit entry">
                                                        <IconButton size="small" disabled={busy} onClick={() => startEdit(e)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {isWorkLikeEntry(e) && (
                                                    (!e.l2_status || e.l2_status === 'PENDING') ? (
                                                        <>
                                                            <Tooltip title="Approve">
                                                                <IconButton size="small" color="success" disabled={busy} onClick={() => handleApproveEntry(e.id)}>
                                                                    <ApproveIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Reject">
                                                                <IconButton size="small" color="error" disabled={busy} onClick={() => openRejectDlg(e.id)}>
                                                                    <RejectIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : (
                                                        <Chip size="small" label={e.l2_status} sx={{ ml: 1 }} />
                                                    )
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {isCompanyScope && !timesheet.level2_approved_at && (
                <Card sx={{ p: 2 }}>
                    <TextField
                        label="Comment (optional)"
                        fullWidth
                        size="small"
                        value={approveComment}
                        onChange={(e) => setApproveComment(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <Box display="flex" gap={2}>
                        <Button variant="contained" startIcon={<ApproveIcon />} disabled={busy} onClick={handleApproveWhole}>
                            Approve Timesheet
                        </Button>
                        <Button variant="outlined" color="error" startIcon={<RejectIcon />} disabled={busy}
                            onClick={() => openRejectDlg('whole')}>
                            Reject Timesheet
                        </Button>
                    </Box>
                </Card>
            )}

            {!isCompanyScope && pendingEntries.length === 0 && (
                <Alert severity="info">All entries assigned to you have already been reviewed.</Alert>
            )}

            <Dialog open={rejectDlg.open} onClose={closeRejectDlg}>
                <DialogTitle>Confirm Rejection</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Reason for rejection"
                        fullWidth
                        multiline
                        minRows={3}
                        value={rejectDlg.comment}
                        onChange={(e) => setRejectDlg((p) => ({ ...p, comment: e.target.value, err: '' }))}
                        error={!!rejectDlg.err}
                        helperText={rejectDlg.err}
                        sx={{ mt: 1, minWidth: 360 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRejectDlg} disabled={busy}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy || !rejectDlg.comment.trim()} onClick={confirmReject}>
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
